import { Component } from '@angular/core';
import { Icons } from '../../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { ImmediateConcernsComponent } from './immediate-concerns/immediate-concerns.component';
import { NotificationsComponent } from "./notifications/notifications.component";
import { IRMSPlanChecklist } from '../../../../shared/models/rmsPlanChecklist';
import { RMSPlanChecklistService } from '../../../../shared/services/rmsPlanChecklist.service';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { CodeOfConductComponent } from "./code-of-conduct/code-of-conduct.component";
import { InterventionMonitoringConsiderationComponent } from './intervention-monitoring-consideration/intervention-monitoring-consideration.component';
import { ParentGuardianComponent } from './parent-guardian/parent-guardian.component';
import { RmsChecklistSignatureComponent } from './rms-checklist-signature/rms-checklist-signature.component';
import { RmsChecklistSubmissionComponent } from './rms-checklist-submission/rms-checklist-submission.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-rms-plan-checklist',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ImmediateConcernsComponent,
    NotificationsComponent,
    PopupMessageComponent,
    SpinnerComponent,
    CodeOfConductComponent,
    InterventionMonitoringConsiderationComponent,
    ParentGuardianComponent,
    RmsChecklistSignatureComponent,
    RmsChecklistSubmissionComponent
],
  templateUrl: './rms-plan-checklist.component.html',
  styleUrl: './rms-plan-checklist.component.css'
})
export class RmsPlanChecklistComponent {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentRmsPlanChecklist: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'immediate_concern_section_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'notification_section_completion',
      nextStepDependency: 'immediate_concern_section_completion'
    },
    {
      id: 3,
      completionProperty: 'code_of_conduct_section_completion',
      nextStepDependency: 'notification_section_completion'
    },
    {
      id: 4,
      completionProperty: 'intervention_section_completion',
      nextStepDependency: 'code_of_conduct_section_completion'
    },
    {
      id: 5,
      completionProperty: 'parent_follow_up_section_completion',
      nextStepDependency: 'intervention_section_completion'
    },
    {
      id: 6,
      completionProperty: 'user_signature_section_completion',
      nextStepDependency: 'parent_follow_up_section_completion'
    }
  ];
  finalStepConfig = {
      id: 7,
      completionProperty: 'rms_plan_checklist_completion',
      nextStepDependency: 'user_signature_section_completion'
  };
  steps = [1, 2, 3, 4, 5, 6];
  currentStep: number = 1;
  tooltips = ['Immediate Concerns and Safety Measures', 'Notifications', 'Code of Conduct', 'Intervention and Monitoring Considerations', 'Parent/Guardian Follow Up Steps', 'Official Signature', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private rmsPlanChecklistService: RMSPlanChecklistService,
    private toastr: ToastrService
  ) {}

  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadRmsPlanChecklist();
  }

  loadRmsPlanChecklist(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.rmsPlanChecklistService.getRmsPlanChecklistByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (Object.keys(response).length === 0) {
            this.currentRmsPlanChecklist = null;
            this.loading = false;
            return
          } else {
            this.currentRmsPlanChecklist = response;
            this.loading = false;
            return
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });
    }
  }

  handleUpdateRMSPlanChecklist(data: IRMSPlanChecklist, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.rmsPlanChecklistService.updateRmsPlanChecklist(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadRmsPlanChecklist();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 7 ) {
            this.router.navigate(['/plans/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating assess screen:', error.message);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/plans/']);
  }
  
  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentRmsPlanChecklist) {
      return this.currentRmsPlanChecklist?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
